import ThemedElement from "shared/components/ThemedElement";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowDownward, ArrowUpward, Close, Computer, Refresh } from "@mui/icons-material";
import { StreamLogsService } from "modules/monitoreo/services/streamLogsService";

const stateLabels = {
  connecting: "Conectando…",
  connected: "Conectado",
  disconnected: "Conexión finalizada",
  error: "Error de conexión",
};
const lineColor = (line) =>
  /\bERROR\b|❌/i.test(line)
    ? "#ffacb2"
    : /\bWARN(?:ING)?\b/i.test(line)
    ? "#f3d58b"
    : /\bINFO\b/i.test(line)
    ? "#a7d4fa"
    : /✅/.test(line)
    ? "#9ce1bd"
    : "#e0e8f0";

function Highlight({ text, query, caseSensitive }) {
  if (!query) return text;
  const source = caseSensitive ? text : text.toLowerCase();
  const target = caseSensitive ? query : query.toLowerCase();
  const parts = [];
  let offset = 0;
  let index = source.indexOf(target);
  while (index !== -1) {
    parts.push(text.slice(offset, index));
    parts.push(
      <ThemedElement
        component="mark"
        key={index}
        style={{ background: "#c9e4ff", color: "#142d44", borderRadius: 2 }}
      >
        {text.slice(index, index + query.length)}
      </ThemedElement>
    );
    offset = index + query.length;
    index = source.indexOf(target, offset);
  }
  parts.push(text.slice(offset));
  return <>{parts}</>;
}
Highlight.propTypes = {
  text: PropTypes.string.isRequired,
  query: PropTypes.string,
  caseSensitive: PropTypes.bool,
};

export default function LogStreamModal({ open, onClose, logInfo, streamUrl }) {
  const [lines, setLines] = useState([]);
  const [connection, setConnection] = useState("disconnected");
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [highlight, setHighlight] = useState(true);
  const [following, setFollowing] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const container = useRef(null);
  const sequence = useRef(0);
  const followRef = useRef(true);
  const url = streamUrl || logInfo?.streamUrl;
  const matches = useMemo(() => {
    if (!query) return [];
    const needle = caseSensitive ? query : query.toLowerCase();
    return lines.filter((line) =>
      (caseSensitive ? line.text : line.text.toLowerCase()).includes(needle)
    );
  }, [lines, query, caseSensitive]);
  const matchIndex = matches.findIndex((line) => line.id === selectedId);
  const activeIndex = matchIndex >= 0 ? matchIndex : matches.length ? 0 : -1;
  const activeId = matches[activeIndex]?.id;

  const follow = useCallback((value) => {
    followRef.current = value;
    setFollowing(value);
    if (value) setNewCount(0);
  }, []);
  useEffect(() => {
    setLines([]);
    setQuery("");
    setSelectedId(null);
    setNewCount(0);
    follow(true);
  }, [open, url, follow]);
  useEffect(() => {
    if (!open) return undefined;
    const stream = new StreamLogsService();
    let active = true;
    setError(null);
    setConnection("connecting");
    stream.connect(
      url,
      (text) => {
        if (!active) return;
        const line = { id: ++sequence.current, text };
        setLines((prev) => [...prev, line].slice(-500));
        if (!followRef.current) setNewCount((count) => count + 1);
      },
      (err) => {
        if (active) {
          setError(err.message || "No se pudo leer el stream.");
          setConnection("error");
        }
      },
      () => {
        if (active) setConnection("connected");
      },
      () => {
        if (active) setConnection((prev) => (prev === "error" ? prev : "disconnected"));
      }
    );
    return () => {
      active = false;
      stream.disconnect();
    };
  }, [open, url, attempt]);
  useEffect(() => {
    if (following && container.current)
      container.current.scrollTop = container.current.scrollHeight;
  }, [lines, following]);
  useEffect(() => {
    if (activeId !== undefined)
      container.current
        ?.querySelector(`[data-log-id="${activeId}"]`)
        ?.scrollIntoView({ block: "nearest" });
  }, [activeId]);
  const navigate = (step) => {
    if (!matches.length) return;
    follow(false);
    setSelectedId(matches[(activeIndex + step + matches.length) % matches.length].id);
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: "90dvh",
          maxHeight: "90dvh",
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 2.5,
          bgcolor: "#193d50",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Computer />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h2" sx={{ fontSize: 19, color: "#fff", fontWeight: 700 }}>
            {logInfo?.name || "Logs de Cerberus"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#d6e5ee", overflowWrap: "anywhere" }}>
            {url || "Sin URL de stream"}
          </Typography>
        </Box>
        <IconButton aria-label="Cerrar visor" onClick={onClose} sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <Box sx={{ px: 2.5, py: 1.5, bgcolor: "#f3f7fb", borderBottom: "1px solid #dce5ed" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
          <Chip
            size="small"
            label={stateLabels[connection]}
            color={
              connection === "connected" ? "success" : connection === "error" ? "error" : "default"
            }
            variant="outlined"
          />
          <Typography sx={{ fontSize: 12, color: "#526b80", flex: 1 }}>
            {lines.length} líneas en memoria · Máximo 500
          </Typography>
          <Button
            startIcon={<Refresh />}
            disabled={connection === "connecting" || !url}
            onClick={() => setAttempt((value) => value + 1)}
            sx={{ "&&": { color: "#245e89" } }}
          >
            Reconectar
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            label="Buscar en los logs"
            placeholder="Enter: siguiente · Shift+Enter: anterior"
            size="small"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelectedId(null);
              if (event.target.value) follow(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                navigate(event.shiftKey ? -1 : 1);
              }
            }}
            sx={{
              flex: 1,
              minWidth: 220,
              bgcolor: "#fff",
              "& .MuiInputBase-root": { minHeight: 44, fontSize: 14 },
            }}
          />
          <Typography sx={{ fontSize: 12, color: "#36546b", minWidth: 65 }}>
            {activeIndex + 1} / {matches.length}
          </Typography>
          <IconButton
            aria-label="Coincidencia anterior"
            disabled={!matches.length}
            onClick={() => navigate(-1)}
          >
            <ArrowUpward />
          </IconButton>
          <IconButton
            aria-label="Siguiente coincidencia"
            disabled={!matches.length}
            onClick={() => navigate(1)}
          >
            <ArrowDownward />
          </IconButton>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={caseSensitive}
                onChange={(e) => {
                  setCaseSensitive(e.target.checked);
                  setSelectedId(null);
                }}
              />
            }
            label={<Typography sx={{ fontSize: 12 }}>Aa</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={highlight}
                onChange={(e) => setHighlight(e.target.checked)}
              />
            }
            label={<Typography sx={{ fontSize: 12 }}>Resaltar</Typography>}
          />
          <Button
            disabled={!query}
            onClick={() => {
              setQuery("");
              setSelectedId(null);
            }}
            sx={{ "&&": { color: "#245e89" } }}
          >
            Limpiar
          </Button>
        </Box>
      </Box>
      <DialogContent
        ref={container}
        onScroll={() => {
          const element = container.current;
          if (!element || query) return;
          follow(element.scrollHeight - element.clientHeight - element.scrollTop < 40);
        }}
        sx={{ p: 0, flex: 1, minHeight: 0, bgcolor: "#142231", overflow: "auto" }}
      >
        {!lines.length ? (
          <Typography sx={{ color: "#a7bdd0", textAlign: "center", py: 6, fontSize: 14 }}>
            {connection === "connecting"
              ? "Estableciendo conexión…"
              : connection === "connected"
              ? "Conectado. Esperando nuevas líneas…"
              : "No se recibieron líneas. Puedes reconectar para intentarlo de nuevo."}
          </Typography>
        ) : (
          <Box sx={{ py: 1 }}>
            {lines.map((line) => (
              <Box
                key={line.id}
                data-log-id={line.id}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  px: 2,
                  py: 0.5,
                  fontFamily: "monospace",
                  fontSize: 13,
                  color: lineColor(line.text),
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  bgcolor: line.id === activeId ? "#23445f" : "transparent",
                  borderLeft: line.id === activeId ? "3px solid #92c9f4" : "3px solid transparent",
                }}
              >
                <Box
                  component="span"
                  sx={{ color: "#8ea5b9", minWidth: 35, textAlign: "right", userSelect: "none" }}
                >
                  {line.id}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Highlight
                    text={line.text}
                    query={highlight ? query : ""}
                    caseSensitive={caseSensitive}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: "#fff",
          borderTop: "1px solid #dce5ed",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <FormControlLabel
          control={<Switch checked={following} onChange={(e) => follow(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13 }}>Seguir nuevas líneas</Typography>}
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          {!following && (
            <Button
              startIcon={<ArrowDownward />}
              onClick={() => {
                setQuery("");
                setSelectedId(null);
                follow(true);
              }}
              sx={{ "&&": { color: "#245e89" } }}
            >
              Ir al final{newCount ? ` (${newCount} nuevas)` : ""}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onClose}
            sx={{ "&&": { bgcolor: "#193d50", color: "#fff" } }}
          >
            Cerrar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
LogStreamModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  logInfo: PropTypes.object,
  streamUrl: PropTypes.string,
};
